<div class="fm-rs-steps">
  <div class="fm-card-wrapper">
    <div class="fm-page-title">
      <h2 class="fm-head-24 fm-title mb-2">Review &amp; Edit Your Answer</h2>
      <p>Review your recording, trim if needed, and add details before submitting</p>
    </div>

    @if (promptTitle()) {
      <div class="fm-white-column mb-4">
        <p class="fm-small text-muted mb-1">Prompt</p>
        <h3 class="fm-head-20 mb-1">{{ promptTitle() }}</h3>
        @if (questionText()) {
          <p class="fm-large mb-0">
            <em>{{ questionText() }}</em>
          </p>
        }
      </div>
    }

    <div class="row">
      <div class="col-12 col-lg-8 col-xl-9">
        <!-- ================================================================ -->
        <!-- VIDEO PREVIEW + TRIM                                              -->
        <!-- ================================================================ -->
        @if (mediaType() === 'video') {
          <div class="fm-white-column">
            <div class="fm-previw-title">
              <div class="row align-items-center">
                <div class="col">
                  <p class="fm-large fm-black-cl fw-semibold mb-0">Video Preview</p>
                </div>
                <div class="col-auto">
                  <span class="fm-tag"
                    ><img src="images/icons/fm-sizzer.svg" class="fm-svg" alt="Trim Icon" /> Trim
                    Video</span
                  >
                </div>
              </div>
            </div>
            <div class="fm-story-vplayer fm-rs-video-preview mt-4">
              @if (videoUrl()) {
                <video
                  #reviewVideo
                  class="fm-story-video"
                  [src]="videoUrl()!"
                  controls
                  autoplay
                  playsinline
                  disablepictureinpicture
                  disableremoteplayback
                  controlslist="nodownload noplaybackrate"
                  (loadedmetadata)="onVideoMetadataLoaded()"
                  (canplay)="onVideoMetadataLoaded()"
                  (timeupdate)="onVideoTimeUpdate()"
                  (seeking)="onVideoSeeked()"
                ></video>
              } @else {
                <div class="fm-video-empty-state">
                  <img src="images/icons/fm-video-lg.svg" class="fm-svg" alt="No Preview" />
                  <p class="mb-0 mt-2">
                    No recorded/uploaded media found. Please go back and record.
                  </p>
                </div>
              }
            </div>
            <div class="fm-golden-column fm-trim-cl mt-4">
              <div class="row align-items-center g-3">
                <div class="col">
                  <h3 class="fm-heading-sm">
                    <img src="images/icons/fm-sizzer-cl.svg" alt="Trim Icon" /> Trim Video
                  </h3>
                </div>
                <div class="col-auto">
                  <button type="button" class="fm-common-btn fm-border-btn" (click)="resetTrim()">
                    <img src="images/icons/fm-reset.svg" alt="Reset Icon" /> Reset
                  </button>
                </div>
                <div class="col-12">
                  <p class="fm-small mt-1 mb-2">
                    Drag sliders to set start and end points. Preview will play only in trimmed
                    range.
                  </p>
                </div>
                <div class="col-12 mb-2">
                  <div class="fm-trim-strip-wrapper" #trimContainer>
                    <canvas #trimCanvas class="fm-trim-canvas"></canvas>
                    <div
                      class="fm-trim-exclude fm-trim-left-exclude"
                      [style.width.%]="trimStartPercent()"
                    ></div>
                    <div
                      class="fm-trim-exclude fm-trim-right-exclude"
                      [style.width.%]="trimEndExcludePercent()"
                    ></div>
                    <div
                      class="fm-trim-selection"
                      [style.left.%]="trimStartPercent()"
                      [style.width.%]="trimSelectionWidth()"
                    ></div>
                    <div
                      class="fm-trim-handle fm-trim-handle-start"
                      [style.left.%]="trimStartPercent()"
                      (mousedown)="startDragging('start', $event)"
                      (touchstart)="startDragging('start', $event)"
                    >
                      <span class="fm-trim-handle-bar"></span>
                      <small class="fm-trim-time-label">{{
                        formatRecordingTime(trimStart())
                      }}</small>
                    </div>
                    <div
                      class="fm-trim-handle fm-trim-handle-end"
                      [style.left.%]="trimEndPercent()"
                      (mousedown)="startDragging('end', $event)"
                      (touchstart)="startDragging('end', $event)"
                    >
                      <span class="fm-trim-handle-bar"></span>
                      <small class="fm-trim-time-label">{{ formatRecordingTime(trimEnd()) }}</small>
                    </div>
                  </div>
                </div>
                <div class="col-sm">
                  <p class="fm-duretion-txt mb-0">
                    New duration:
                    <span class="fw-semibold">{{ formatRecordingTime(trimmedDuration()) }}</span>
                    (removed {{ formatRecordingTime(removedDuration()) }})
                  </p>
                </div>
                <div class="col-sm-auto">
                  <div class="fm-btn-group">
                    <button type="button" class="fm-common-btn fm-border-btn" (click)="resetTrim()">
                      Cancel
                    </button>
                    <button
                      type="button"
                      class="fm-common-btn fm-secondary-btn fm-white-btn-txt"
                      (click)="applyTrim()"
                    >
                      Apply Trim
                      <img src="images/icons/fm-arrow-right.svg" class="fm-svg" alt="Apply" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        } @else {
          <!-- ================================================================ -->
          <!-- AUDIO / IMAGE-AUDIO PREVIEW + TRIM                               -->
          <!-- ================================================================ -->
          <div class="fm-white-column">
            <div class="fm-previw-title">
              <div class="row align-items-center">
                <div class="col">
                  <p class="fm-large fm-black-cl fw-semibold mb-0">Audio Preview</p>
                </div>
                <div class="col-auto">
                  <span class="fm-tag"
                    ><img src="images/icons/fm-sizzer.svg" class="fm-svg" alt="Trim Icon" /> Trim
                    Audio</span
                  >
                </div>
              </div>
            </div>

            <div class="fm-stories-blog fm-stories-details mt-4">
              @if (mediaType() === 'image-audio' && imageUrl()) {
                <div class="fm-story-aimage">
                  <img
                    class="fm-story-image img-fluid w-100"
                    [src]="imageUrl()!"
                    alt="Story Image"
                  />
                </div>
              }

              <div class="fm-golden-column">
                <div class="row">
                  <div class="col-12 mt-3">
                    @if (videoUrl()) {
                      <audio
                        #reviewAudio
                        class="fm-audio-player w-100 mb-3"
                        [src]="videoUrl()!"
                        controls
                        (loadedmetadata)="onAudioMetadataLoaded()"
                        (timeupdate)="onAudioTimeUpdate()"
                        (play)="onAudioPlay()"
                        (pause)="onAudioPause()"
                        (ended)="onAudioPause()"
                      ></audio>
                    } @else {
                      <div class="text-center py-3">
                        <img
                          src="images/icons/fm-micro-icon.svg"
                          class="fm-svg mb-2"
                          alt="No Audio"
                          style="width: 48px; height: 48px; opacity: 0.45"
                        />
                        <p class="mb-0 fm-small">No audio recorded. Please go back and record.</p>
                      </div>
                    }
                    @if (isAudioPlaying()) {
                      <div class="fm-audio-review-visualizer" aria-hidden="true">
                        @for (
                          _ of [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];
                          track $index
                        ) {
                          <span class="fm-arv-bar"></span>
                        }
                      </div>
                    } @else {
                      <img
                        src="images/icons/fm-audio-wave.svg"
                        class="fm-audio-wave-img img-fluid"
                        alt="Audio Wave"
                      />
                    }
                  </div>
                </div>
              </div>

              @if (videoUrl()) {
                <div class="fm-golden-column fm-trim-cl">
                  <div class="row align-items-center g-3">
                    <div class="col">
                      <h3 class="fm-heading-sm">
                        <img src="images/icons/fm-sizzer-cl.svg" alt="Trim Icon" /> Trim Audio
                      </h3>
                    </div>
                    <div class="col-auto">
                      <button
                        type="button"
                        class="fm-common-btn fm-border-btn"
                        (click)="resetAudioTrim()"
                      >
                        <img src="images/icons/fm-reset.svg" alt="Reset Icon" /> Reset
                      </button>
                    </div>
                    <div class="col-12">
                      <p class="fm-small mt-1 mb-2">
                        Drag sliders to set start and end points. Parts outside will be removed.
                      </p>
                    </div>
                    <div class="col-12 mb-2">
                      <div class="fm-trim-strip-wrapper fm-audio-trim-strip" #audioTrimContainer>
                        <img
                          src="images/icons/fm-audio-wave.svg"
                          class="fm-audio-trim-wave"
                          alt=""
                          aria-hidden="true"
                        />
                        <div
                          class="fm-trim-exclude fm-trim-left-exclude"
                          [style.width.%]="audioTrimStartPercent()"
                        ></div>
                        <div
                          class="fm-trim-exclude fm-trim-right-exclude"
                          [style.width.%]="audioTrimEndExcludePercent()"
                        ></div>
                        <div
                          class="fm-trim-selection"
                          [style.left.%]="audioTrimStartPercent()"
                          [style.width.%]="audioTrimSelectionWidth()"
                        ></div>
                        <div
                          class="fm-trim-handle fm-trim-handle-start"
                          [style.left.%]="audioTrimStartPercent()"
                          (mousedown)="startAudioDragging('start', $event)"
                          (touchstart)="startAudioDragging('start', $event)"
                        >
                          <span class="fm-trim-handle-bar"></span>
                          <small class="fm-trim-time-label">{{
                            formatRecordingTime(audioTrimStart())
                          }}</small>
                        </div>
                        <div
                          class="fm-trim-handle fm-trim-handle-end"
                          [style.left.%]="audioTrimEndPercent()"
                          (mousedown)="startAudioDragging('end', $event)"
                          (touchstart)="startAudioDragging('end', $event)"
                        >
                          <span class="fm-trim-handle-bar"></span>
                          <small class="fm-trim-time-label">{{
                            formatRecordingTime(audioTrimEnd())
                          }}</small>
                        </div>
                      </div>
                    </div>
                    <div class="col-sm">
                      <p class="fm-duretion-txt mb-0">
                        New duration:
                        <span class="fw-semibold">{{
                          formatRecordingTime(audioTrimmedDuration())
                        }}</span>
                        (removed {{ formatRecordingTime(audioRemovedDuration()) }})
                      </p>
                    </div>
                    <div class="col-sm-auto">
                      <div class="fm-btn-group">
                        <button
                          type="button"
                          class="fm-common-btn fm-border-btn"
                          (click)="resetAudioTrim()"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          class="fm-common-btn fm-secondary-btn fm-white-btn-txt"
                          (click)="applyAudioTrim()"
                        >
                          Apply Trim
                          <img src="images/icons/fm-arrow-right.svg" class="fm-svg" alt="Apply" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>
        }

        <!-- Story Details + Thumbnail (side by side for audio) -->
        <div class="row fm-row-equal-cards">
          <div class="col-xl-12">
            <div class="fm-white-column fm-match-height mt-4">
              <div class="accordion fm-accordion fm-accordion-st2">
                <div class="accordion-item fm-accordion-item mt-0">
                  <span class="accordion-header fm-accordion-header">
                    <button
                      class="accordion-button fm-accordion-button"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#spReviewThumbnail"
                      aria-expanded="true"
                      aria-controls="spReviewThumbnail"
                    >
                      Upload Thumbnail <span class="text-danger ms-1">*</span>
                    </button>
                  </span>
                  <div
                    id="spReviewThumbnail"
                    class="accordion-collapse fm-accordion-collapse collapse show"
                  >
                    <div class="accordion-body fm-accordion-body pt-3">
                      <div
                        class="fm-thumbnail-upload-inline"
                        [class.is-invalid-upload]="thumbnailError()"
                        [class.fm-drag-over]="thumbnailDragOver()"
                        (dragover)="onThumbnailDragOver($event)"
                        (dragleave)="onThumbnailDragLeave($event)"
                        (drop)="onThumbnailDrop($event)"
                      >
                        @if (!thumbnailPreviewUrl()) {
                          <label
                            for="spThumbnailInput"
                            class="fm-thumbnail-upload-btn mb-0"
                            [class.fm-upload-error]="thumbnailError()"
                          >
                            <img
                              src="images/icons/fm-upload-icon.svg"
                              class="fm-svg"
                              alt="Upload Thumbnail"
                            />
                            <span class="fm-upload-helper-text"
                              >Drop your photo here, or
                              <span class="fm-browse-text">Browse</span></span
                            >
                          </label>
                        } @else {
                          <div class="fm-thumbnail-preview">
                            <img
                              [src]="thumbnailPreviewUrl()!"
                              class="fm-thumbnail-preview-image"
                              alt="Thumbnail Preview"
                            />
                            <div class="fm-thumbnail-preview-info">
                              <span class="fm-thumbnail-preview-name">{{
                                thumbnailFileName()
                              }}</span>
                              <span class="fm-thumbnail-preview-size">{{
                                thumbnailFileSize()
                              }}</span>
                            </div>
                            <button
                              type="button"
                              class="fm-x-btn fm-thumbnail-remove-btn"
                              (click)="removeThumbnail()"
                              aria-label="Remove thumbnail"
                            >
                              <img class="bg" src="images/icons/fm-x.svg" alt="Remove" />
                            </button>
                          </div>
                        }
                        <input
                          type="file"
                          id="spThumbnailInput"
                          class="fm-thumbnail-file-input"
                          accept="image/jpeg,image/png,image/webp,image/gif,image/*"
                          (change)="onThumbnailUpload($event)"
                        />
                      </div>
                      @if (thumbnailTypeError()) {
                        <p class="text-danger fm-small mt-2 mb-0" role="alert">
                          Only image files are allowed (JPEG, PNG, WebP, GIF).
                        </p>
                      }
                      @if (thumbnailError()) {
                        <p class="text-danger fm-small mt-2 mb-0" role="alert">
                          Thumbnail is required. Please upload a photo.
                        </p>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer actions -->
        <div class="fm-form-footer fm-space-between mt-4">
          <button
            type="button"
            class="fm-common-btn fm-secondary-btn fm-secondary-border-btn"
            (click)="backToRecording()"
          >
            <img src="images/icons/fm-arrow-left.svg" class="fm-svg" alt="Back" />
            Back to Recording
          </button>
          @if (publishError()) {
            <p class="text-danger mb-0 me-3 small">{{ publishError() }}</p>
          }
          <button
            type="button"
            class="fm-common-btn fm-secondary-btn fm-white-btn-txt"
            [disabled]="isPublishing()"
            (click)="openPublishConfirm()"
          >
            Submit Answer <img src="images/icons/fm-arrow-right.svg" class="fm-svg" alt="Submit" />
          </button>
        </div>
      </div>

      <!-- Sidebar: Before Submitting checklist -->
      <div class="col-12 col-lg-4 col-xl-3 mt-4 mt-lg-0">
        <div class="fm-sidebar-clumn">
          <div class="fm-border-column fm-color-brd">
            <div class="row">
              <div class="col-12">
                <h3 class="fm-head-20 fm-sidebar-title">Before Submitting</h3>
              </div>
              <div class="col-12">
                <div class="fm-recording-tips-list">
                  <div class="fm-detail-item align-items-center">
                    <img class="fm-svg me-1" src="images/icons/fm-check-md.svg" alt="Check" />
                    <div class="fm-detail-content">
                      <p class="fm-detail-text">
                        @if (mediaType() === 'video') {
                          Review video and trim if needed
                        } @else if (mediaType() === 'audio') {
                          Review audio and trim if needed
                        } @else {
                          Review image and audio, trim if needed
                        }
                      </p>
                    </div>
                  </div>
                  <div class="fm-detail-item align-items-center">
                    <img class="fm-svg me-1" src="images/icons/fm-check-md.svg" alt="Check" />
                    <div class="fm-detail-content">
                      <p class="fm-detail-text">Add a story title</p>
                    </div>
                  </div>
                  <div class="fm-detail-item align-items-center">
                    <img class="fm-svg me-1" src="images/icons/fm-check-md.svg" alt="Check" />
                    <div class="fm-detail-content">
                      <p class="fm-detail-text">Tag people this story is about</p>
                    </div>
                  </div>
                  <div class="fm-detail-item align-items-center">
                    <img class="fm-svg me-1" src="images/icons/fm-check-md.svg" alt="Check" />
                    <div class="fm-detail-content">
                      <p class="fm-detail-text">Add date and location details</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Trimming Progress Overlay -->
  @if (isTrimmingMedia()) {
    <div class="fm-reload-overlay" role="status" aria-live="polite">
      <div class="fm-reload-modal">
        <div class="fm-reload-modal-icon fm-reload-modal-icon--primary">
          <img src="images/icons/fm-sizzer-cl.svg" class="fm-svg" alt="Trimming" />
        </div>
        <h3 class="fm-reload-modal-title">Applying Trim...</h3>
        <p class="fm-reload-modal-desc">Re-encoding the trimmed segment. This may take a moment.</p>
        <div class="fm-trim-progress mt-3">
          <div class="progress fm-progress">
            <div
              class="progress-bar fm-progress-bar"
              role="progressbar"
              [style.width.%]="trimmingProgress()"
              [attr.aria-valuenow]="trimmingProgress()"
              aria-valuemin="0"
              aria-valuemax="100"
            ></div>
          </div>
          <p class="fm-small text-center mt-2 mb-0">{{ trimmingProgress() }}%</p>
        </div>
      </div>
    </div>
  }

  <!-- Submit Confirmation Modal -->
  @if (showPublishConfirm()) {
    <div class="fm-reload-overlay" role="dialog" aria-modal="true" aria-labelledby="spConfirmTitle">
      <div class="fm-reload-modal">
        <div class="fm-reload-modal-icon fm-reload-modal-icon--primary">
          <img src="images/icons/fm-check-md.svg" class="fm-svg" alt="Submit" />
        </div>
        <h3 class="fm-reload-modal-title" id="spConfirmTitle">Ready to Submit?</h3>
        <p class="fm-reload-modal-desc">Your answer will be submitted. This cannot be undone.</p>
        <div class="fm-reload-modal-actions">
          <button type="button" class="fm-common-btn fm-primary-btn" (click)="saveAndPublish()">
            Yes, Submit Now
          </button>
          <button type="button" class="fm-reload-leave-btn" (click)="cancelPublish()">
            Cancel
          </button>
        </div>
      </div>
    </div>
  }

  <!-- Publishing Loader -->
  @if (isPublishing()) {
    <div
      class="fm-publishing-overlay"
      role="status"
      aria-live="polite"
      aria-label="Submitting your answer"
    >
      <div class="fm-publishing-loader-box">
        <div class="spinner-border fm-publishing-spinner" role="status" aria-hidden="true"></div>
        <p class="fm-publishing-label">Submitting your answer…</p>
      </div>
    </div>
  }

  <!-- Reload Warning Modal -->
  @if (showReloadWarning()) {
    <div class="fm-reload-overlay" role="dialog" aria-modal="true" aria-labelledby="spReloadTitle">
      <div class="fm-reload-modal">
        <div class="fm-reload-modal-icon"><span>&#9888;</span></div>
        <h3 class="fm-reload-modal-title" id="spReloadTitle">Leave this page?</h3>
        <p class="fm-reload-modal-desc">
          Your recording will be <strong>permanently lost</strong> if you refresh or leave.
        </p>
        <div class="fm-reload-modal-actions">
          <button
            type="button"
            class="fm-common-btn fm-primary-btn"
            (click)="dismissReloadWarning()"
          >
            Stay &amp; Continue
          </button>
          <button type="button" class="fm-reload-leave-btn" (click)="confirmReload()">
            Leave anyway
          </button>
        </div>
      </div>
    </div>
  }
</div>
